import { GoToMealsRequest } from "@/clients/models/GoToMealsRequest";
import { MealTime } from "@/clients/models/MealTime";
import { mealTabs } from "@/constants/meal-tab";
import { QueryKeys } from "@/constants/query-keys";
import { useGenerateCandidate } from "@/hooks/requests/go-to/use-generate-candidate";
import { useGetGotoPreference } from "@/hooks/requests/go-to/use-get-goto-preference";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { useToast } from "@/hooks/ui/use-toast";
import { GoToFoodFormData, GoToFoodSchema } from "@/schemas/go-to-food-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

interface UseGoToFoodProfileProps {
  onSuccess: () => void;
}

export default function useGoToFoodProfile({
  onSuccess,
}: UseGoToFoodProfileProps) {
  const searchParams = useSearchParams();
  const mealTime =
    (searchParams.get("mealTime") as MealTime) ?? MealTime.BREAKFAST;
  const navigation = useIonRouter();
  const queryClient = useQueryClient();
  const { gotoPreference, isLoading } = useGetGotoPreference(mealTime);
  const { generateCandidate, isGenerating } = useGenerateCandidate(mealTime);
  const { showError } = useToast();

  const form = useForm<GoToFoodFormData>({
    resolver: zodResolver(GoToFoodSchema),
    defaultValues: {
      selectedMeals: [],
    },
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = form;

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (gotoPreference?.preferences) {
      const mealNames = gotoPreference.preferences.map((meal) => ({
        name: meal,
      }));
      setValue("selectedMeals", mealNames);
    }
  }, [gotoPreference, isLoading]);

  const getPreviousMealTimePath = () => {
    const currentIndex = mealTabs.indexOf(mealTime);
    const previousMealTime = mealTabs[currentIndex - 1];
    if (previousMealTime) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("mealTime", previousMealTime);
      return `${location.pathname}?${newSearchParams.toString()}`;
    }
    return null;
  };

  const onSubmit = (data: GoToFoodFormData) => {
    const mealIdsToSave = data.selectedMeals.map((meal) => meal.name);

    const dataBody: GoToMealsRequest = {
      meals: mealIdsToSave,
    };

    generateCandidate(dataBody, {
      onSuccess: () => {
        reset({
          selectedMeals: [],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GO_TO_PREFERENCE, mealTime],
        });
        const currentIndex = mealTabs.indexOf(mealTime);
        const nextMealTime = mealTabs[currentIndex + 1];
        console.log("nextMealTime", nextMealTime);
        if (nextMealTime) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set("mealTime", nextMealTime);
          navigation.push(
            `${location.pathname}?${newSearchParams.toString()}`,
            "none",
          );
        } else {
          onSuccess();
        }
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  const handleFormSubmit = handleSubmit(onSubmit, (errors) => {
    console.log("Form validation errors:", errors);
    if (errors.selectedMeals) {
      showError(
        errors.selectedMeals.message || "Please check your meal selection",
      );
    }
  });

  return {
    form,
    handleFormSubmit,
    isGenerating,
    isValid,
    isLoading,
    mealTime,
    getPreviousMealTimePath,
  };
}
